import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Container, Row, Col, Button, Badge} from "reactstrap";
import {CardSkeleton} from "./SkeletonLoader";
import {Line} from "react-chartjs-2";
import {Chart, ChartData, registerables} from "chart.js";
import CanvasHeroChart from "./CanvasHeroChart";
import HomeGameTeaser from "./HomeGameTeaser";
import HomeEarningsSignals from "./HomeEarningsSignals";
import {HomeProps} from "../containers/Home";
import { usePostHog } from "@posthog/react";
import {formatDate} from "../utils/dates";
import {useSeo} from "../hooks/useSeo";
import {buildViewFeaturedUrl} from "../utils/links";
import {displaySymbol} from "../utils/delisted";
import {
  confluenceLabel,
  selectHomeSignals,
  summarizeSignal,
  windowBadgeLabel,
  type HomeSignal,
  type SignalSummary,
} from "../utils/signalSummary";

// Register ChartJS components
Chart.register(...registerables);

const Home = ({content, getFeaturedMatches}: HomeProps) => {
  useSeo({
    title: "DejaChart — Stock Chart Pattern Matching & Historical Analogs",
    description: "See what happened every time a stock looked exactly like this. DejaChart matches today's charts against 20 years of Russell 3000 price history and shows you every historical analog — with the full distribution of what followed.",
    path: "/",
  });
  const posthog = usePostHog();
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sampleDate, setSampleDate] = useState("");

  // Load featured data on mount - use latest available date
  useEffect(() => {
    if (!initialLoadDone && !content) {
      getFeaturedMatches();
      setInitialLoadDone(true);
    } else if (content) {
      // featured_dates is typed non-null, but a 200 with a partial body omits it.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const reversedDates = [...(content.featured_dates ?? [])].reverse();
      const targetDate = reversedDates[0]; // Use most recent date

      if (targetDate && targetDate !== sampleDate) {
        setSampleDate(targetDate);
      }
      setIsLoading(false);
    }
  }, [content, getFeaturedMatches, initialLoadDone, sampleDate]);

  // Function to create overlay chart with query + top matches
  const createOverlayChartData = (stockData: StockData) => {
    // Derived, not hardcoded: the sample cards now come from all five windows, whose
    // query series are 10 / 20 / 65 bars long (already step-downsampled — the 6m and
    // 1y presets are 65 bars too). Each analog carries `forecast_window` extra bars,
    // so the dashed lines run past the query and show what followed.
    const queryBars = stockData.query.query_data_norm.length;
    const matchBars = queryBars + Math.max(0, stockData.query.forecast_window);

    const colors = [
      "#3B82F6", // primary blue
      "#059669", // green
      "#DC2626", // red
      "#8B5CF6", // purple
      "#F97316", // orange
    ];

    // Query (primary) dataset
    const queryDataset = {
      label: displaySymbol(stockData.query.ticker, false),
      data: stockData.query.query_data_norm.slice(0, queryBars),
      borderColor: colors[0],
      backgroundColor: "transparent",
      borderWidth: 2.5,
      pointRadius: 0,
      tension: 0.3,
    };

    // Top 3 match datasets. displaySymbol renders a delisted analog as "FOO*" (from the
    // API's authoritative delisted flag); the "*" is explained in the footnote below.
    const matchDatasets = stockData.matches.slice(0, 3).map((match, i) => ({
      label: displaySymbol(match.match_symbol, match.delisted),
      data: match.match_data_norm.slice(0, matchBars),
      borderColor: colors[i + 1],
      backgroundColor: "transparent",
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0.3,
      borderDash: [4, 4],
    }));

    return {
      labels: Array.from({length: matchBars}, (_, i) => i + 1),
      datasets: [queryDataset, ...matchDatasets],
    } as ChartData<"line", number[], number>;
  };

  // Chart options for overlay chart (simplified view-featured style)
  const overlayChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11,
            family: "'IBM Plex Mono', monospace",
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        display: true,
        grid: {
          color: "rgba(203, 213, 225, 0.2)",
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  // The best 3 signals scored across ALL five windows (10/20/65/130/260) — the page
  // already fetches every window, so picking hardcoded indexes out of the 4-week list
  // threw away ~90% of the available signals. Scoring, eligibility filters and the
  // window/direction diversification live in utils/signalSummary.
  const sampleStocks: HomeSignal[] = selectHomeSignals(content);

  // Templated, past-tense description of what each signal's own analogs did. Recomputed
  // per match from match_ohlcv — NEVER from forward_curve.returns, which is sorted and
  // not aligned to `matches` (see the util's module header).
  const sampleSummaries: (SignalSummary | null)[] = sampleStocks.map((stock) =>
    summarizeSignal(stock, stock.direction, stock.window),
  );

  // Render sample pattern cards
  const renderSampleCards = () => {
    if (isLoading) {
      return (
        <>
          {[1, 2, 3].map((i) => (
            <Col key={i} xs="12" className="mb-4">
              <CardSkeleton />
            </Col>
          ))}
        </>
      );
    }

    if (sampleStocks.length === 0) {
      return (
        <Col className="text-center py-5">
          <p>No sample patterns available.</p>
        </Col>
      );
    }

    return sampleStocks.map((stock, index) => {
      const summary = sampleSummaries[index];
      const confluence = confluenceLabel(stock.confluenceWindows);
      return (
        <Col xs="12" className="chart-col" key={`${stock.query.ticker}_${stock.window}_${stock.direction}`}>
          <div className="chart-card">
            <div className="chart-header">
              <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-1">
                <div className="d-flex align-items-center flex-wrap">
                  <h3 className="mb-0 me-2">{stock.query.ticker}</h3>
                  <Badge
                    color={stock.direction === "up" ? "success" : "danger"}
                    className="me-1"
                  >
                    {stock.direction === "up" ? "BULLISH" : "BEARISH"}
                  </Badge>
                  {/* Which window this card came from — cards are now picked across all
                      five, so "4-Week" vs "1-Year" is load-bearing context, not decoration. */}
                  <Badge color="secondary" pill className="recent-badge me-1">
                    {windowBadgeLabel(stock.window)}
                  </Badge>
                  {confluence && (
                    <Badge color="info" pill className="recent-badge">
                      {confluence}
                    </Badge>
                  )}
                </div>
                {/* Match quality, not a gate: every ticker is shown (no similarity floor),
                    so a loose analog set has to READ as loose. */}
                {stock.quality && (
                  <span className={`signal-quality signal-quality-${stock.quality.level}`}>
                    {stock.quality.label}
                  </span>
                )}
              </div>
              <span className="chart-date">
                {stock.query.query_dates.length > 1
                  ? `${formatDate(stock.query.query_dates[0], 0)} – ${formatDate(stock.query.query_dates[stock.query.query_dates.length - 1], 0)}`
                  : formatDate(stock.query.query_title, 1)}
              </span>
            </div>
            <div className="chart-body-wrap">
              <div className="chart-body">
                <div style={{height: "260px"}}>
                  <Line
                    data={createOverlayChartData(stock)}
                    options={overlayChartOptions}
                  />
                </div>
              </div>
              <div className="chart-footer">
                <div className="stat-box">
                  <span className="stat-label">Up Prob</span>
                  <span
                    className={`stat-value ${stock.fraction_up > 50 ? "green" : "red"}`}
                  >
                    {stock.fraction_up.toFixed(1)}%
                  </span>
                </div>
                <div className="stat-box">
                  <span className="stat-label">Avg Move</span>
                  <span
                    className={`stat-value ${stock.direction === "up" ? "green" : "red"}`}
                  >
                    {stock.direction === "up"
                      ? `+${stock.avg_up.toFixed(1)}%`
                      : `${stock.avg_dn.toFixed(1)}%`}
                  </span>
                </div>
                <div className="stat-box">
                  <span className="stat-label">
                    {stock.direction === "up" ? "Max Up" : "Max Down"}
                  </span>
                  <span
                    className={`stat-value ${stock.direction === "up" ? "green" : "red"}`}
                  >
                    {stock.direction === "up"
                      ? `+${stock.max_up.toFixed(1)}%`
                      : `${stock.max_dn.toFixed(1)}%`}
                  </span>
                </div>
              </div>
            </div>
            {/* What this signal's own analogs actually did — past tense, this signal only.
                No aggregate accuracy / hit rate anywhere (server-side suppressed too). */}
            {summary && <p className="signal-summary">{summary.text}</p>}
            <div className="chart-action">
              <Link
                to={buildViewFeaturedUrl({ direction: stock.direction, ticker: stock.query.ticker, window: stock.window, date: sampleDate })}
                rel="nofollow"
                className="btn btn-outline-primary btn-sm w-100"
                onClick={() => posthog.capture("home_cta_clicked", { cta: "view_full_analysis", ticker: stock.query.ticker, window: stock.window, direction: stock.direction })}
              >
                View Full Analysis
              </Link>
            </div>
          </div>
        </Col>
      );
    });
  };

  return (
    <div className="deja-app">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg="7">
              <h1 className="hero-title">
                A search engine for chart history
              </h1>
              <p className="hero-subtitle">
                Point it at any ticker. DejaChart finds every time that shape
                appeared across 20 years of Russell 3000 history and shows you
                what followed each time — the ups, the downs, and how often each
                happened. You draw the conclusion.
              </p>
              <div className="hero-buttons">
                <Link to="/featured" onClick={() => posthog.capture("home_cta_clicked", { cta: "hero_explore_matches" })}>
                  <Button color="primary" className="primary-btn">
                    Explore today's matches
                  </Button>
                </Link>
                <Link to="/game" onClick={() => posthog.capture("home_cta_clicked", { cta: "hero_play_game" })}>
                  <Button color="secondary" outline>
                    Try the chart game
                  </Button>
                </Link>
              </div>
            </Col>
            <Col lg="5" className="hero-chart">
              <CanvasHeroChart />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Reporting Soon + Sample Matches — side by side on desktop, stacked on
          mobile (Reporting Soon first via column order). */}
      <section className="home-panels">
        <Container>
          <Row>
            <Col lg="6" className="mb-4 mb-lg-0">
              <HomeEarningsSignals />
            </Col>
            <Col lg="6">
              <div className="home-panel">
                <h2 className="section-title">Sample Pattern Matches</h2>
                <p className="section-subtitle text-muted text-center mb-4">
                  Real examples showing how our pattern matching identifies similar
                  historical price movements. The primary stock (solid line) is matched
                  against historical patterns (dashed lines).
                </p>
                <Row className="chart-grid">{renderSampleCards()}</Row>
                {/* The summary sentence now spells out a delisted closest analog in plain
                    words (see signalSummary.ts) instead of the "*" marker, so only the chart
                    legend's compact top-3 labels still need this footnote to decode. */}
                {sampleStocks.some((s) => s.matches.slice(0, 3).some((m) => m.delisted)) && (
                  <div className="text-muted text-center" style={{fontSize: 11, marginTop: 8}}>
                    <strong>*</strong> delisted security — no longer trades; shown as a historical analog.
                  </div>
                )}
                <div className="text-center mt-4">
                  <Link to="/featured" onClick={() => posthog.capture("home_cta_clicked", { cta: "explore_all_featured" })}>
                    <Button color="primary" className="primary-btn">
                      Explore All Featured Matches
                    </Button>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <Container>
          <h2 className="section-title">How It Works</h2>
          <Row className="features-row">
            <Col md="4">
              <div className="feature-card">
                <div className="feature-icon pattern-icon"></div>
                <h3>Pattern Recognition at Scale</h3>
                <p>
                  We compare a chart's recent shape with 20 years of daily price
                  history, over windows from two weeks to a year, and show how each
                  close analog played out.
                </p>
              </div>
            </Col>
            <Col md="4">
              <div className="feature-card">
                <div className="feature-icon prediction-icon"></div>
                <h3>Daily Pattern Matches</h3>
                <p>
                  Each day we scan recent price action, match it against 20 years
                  of historical patterns, and surface a fresh set of stocks to
                  watch — each with what actually happened next.
                </p>
              </div>
            </Col>
            <Col md="4">
              <div className="feature-card">
                <div className="feature-icon confidence-icon"></div>
                <h3>Where the Analogs Agree</h3>
                <p>
                  Only setups where the closest historical analogs mostly moved the
                  same way afterward are featured: a focused list to research, not
                  a forecast.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Playable proof of the pitch, after How It Works has explained it — the hero already
          has its own chart, so this doesn't compete with it. */}
      <HomeGameTeaser />
    </div>
  );
};

export default Home;
